import { root, folder, file, today } from './seed.js';
import { filesystemState, flattenTree, resetFsMap } from './store.svelte.js';
import { virtualSearchNodes, buildVirtualSearchNodes, indexSearchNodes } from './search.js';
import { getNode } from './navigation.js';

function parentPath(path) {
  const parts = path.split('/').filter(Boolean);
  parts.pop();
  return parts.length ? `/${parts.join('/')}` : '/';
}

function uniqueName(parent, requested, excludeId = null) {
  const names = new Set((parent.children ?? []).filter((child) => child.id !== excludeId).map((child) => child.name.toLowerCase()));
  if (!names.has(requested.toLowerCase())) return requested;
  const dot = requested.lastIndexOf('.');
  const stem = dot > 0 ? requested.slice(0, dot) : requested;
  const ext = dot > 0 ? requested.slice(dot) : '';
  let index = 2;
  while (names.has(`${stem} ${index}${ext}`.toLowerCase())) index += 1;
  return `${stem} ${index}${ext}`;
}

function rewritePaths(node, path) {
  node.path = path;
  for (const child of node.children ?? []) rewritePaths(child, path === '/' ? `/${child.name}` : `${path}/${child.name}`);
}

function touchFilesystem() {
  resetFsMap(flattenTree(root));
  virtualSearchNodes.length = 0;
  const fresh = buildVirtualSearchNodes();
  virtualSearchNodes.push(...fresh);
  indexSearchNodes();
  filesystemState.revision += 1;
}

export { touchFilesystem, parentPath, uniqueName, rewritePaths };

export function createFolder(parentPath, requestedName = 'untitled folder') {
  const parentNode = getNode(parentPath);
  if (!parentNode?.children) return null;
  const name = uniqueName(parentNode, requestedName.trim() || 'untitled folder');
  const path = parentPath === '/' ? `/${name}` : `${parentPath}/${name}`;
  const node = folder(name, path);
  parentNode.children.push(node);
  touchFilesystem();
  return node;
}

export function createFile(parentPath, requestedName = 'Untitled.txt', content = '', mimeType = 'text/plain') {
  const parentNode = getNode(parentPath);
  if (!parentNode?.children) return null;
  const name = uniqueName(parentNode, requestedName.trim() || 'Untitled.txt');
  const path = parentPath === '/' ? `/${name}` : `${parentPath}/${name}`;
  const node = file(name, path, 'document', `${new Blob([content]).size} B`, null, today, content, mimeType);
  parentNode.children.push(node);
  touchFilesystem();
  return node;
}

export function updateFile(path, changes = {}) {
  const node = getNode(path);
  if (!node || node.kind !== 'file') return null;
  Object.assign(node, changes, { modified: new Date().toISOString().slice(0, 10) });
  if ('content' in changes) node.size = `${new Blob([changes.content ?? '']).size} B`;
  touchFilesystem();
  return node;
}

export function renameNode(path, requestedName) {
  const node = getNode(path);
  const parentNode = getNode(parentPath(path));
  if (!node || !parentNode?.children || path === '/') return null;
  node.name = uniqueName(parentNode, requestedName.trim() || node.name, node.id);
  rewritePaths(node, parentNode.path === '/' ? `/${node.name}` : `${parentNode.path}/${node.name}`);
  touchFilesystem();
  return node;
}

export function moveNode(path, destinationPath) {
  const node = getNode(path);
  const sourceParent = getNode(parentPath(path));
  const destination = getNode(destinationPath);
  if (!node || !sourceParent?.children || !destination?.children || path === '/') return null;
  const index = sourceParent.children.findIndex((child) => child.id === node.id);
  if (index === -1) return null;
  sourceParent.children.splice(index, 1);
  node.name = uniqueName(destination, node.name);
  destination.children.push(node);
  rewritePaths(node, destination.path === '/' ? `/${node.name}` : `${destination.path}/${node.name}`);
  touchFilesystem();
  return node;
}

export function moveToTrash(path) {
  const node = getNode(path);
  if (!node || path === '/Users/Guest/.Trash' || path.startsWith('/Users/Guest/.Trash/')) return null;
  node.originalPath = path;
  return moveNode(path, '/Users/Guest/.Trash');
}

export function trashItems() { filesystemState.revision; return getNode('/Users/Guest/.Trash')?.children ?? []; }

export function restoreFromTrash(path) {
  const node = getNode(path);
  if (!node?.originalPath) return null;
  const destination = getNode(parentPath(node.originalPath))?.children ? parentPath(node.originalPath) : '/Users/Guest';
  const restored = moveNode(path, destination);
  if (restored) restored.originalPath = null;
  touchFilesystem();
  return restored;
}

export function deletePermanently(path) {
  const parentNode = getNode(parentPath(path));
  if (!parentNode?.children || path === '/') return false;
  const index = parentNode.children.findIndex((child) => child.path === path);
  if (index === -1) return false;
  parentNode.children.splice(index, 1);
  touchFilesystem();
  return true;
}

export function emptyTrash() {
  const trash = getNode('/Users/Guest/.Trash');
  if (!trash) return;
  trash.children = [];
  touchFilesystem();
}

export function allFiles(node = root, output = []) {
  filesystemState.revision;
  if (node.kind === 'file') output.push(node);
  for (const child of node.children ?? []) allFiles(child, output);
  return output;
}
